<script setup>
import axios from 'axios';
import { BForm, BFormCheckbox, BFormGroup, BFormInvalidFeedback, BFormTextarea } from 'bootstrap-vue-next';
import { ref, computed, onMounted } from 'vue';

import LoadingButton from '~/common/components/LoadingButton.vue';

const props = defineProps({
  duoHasReviewed: {
    type: Boolean,
    default: false,
  },
  mergeRequestIid: {
    type: String,
    required: true,
  },
  otherReviewers: {
    type: Array,
    default: () => [],
  },
  projectId: {
    type: String,
    required: true,
  },
  suggestedReviewer: {
    type: String,
    required: true,
  },
  webUrl: {
    type: String,
    required: true,
  },
});

const emits = defineEmits(['error']);
const customReviewers = ref('');
const loading = ref(false);
const selectedReviewers = ref([]);

const MAX_REVIEWERS = 4;

const manualUsernames = computed(() =>
  customReviewers.value
    .split('\n')
    .map(x => x.trim())
    .filter(x => x),
);

const hasSpacesInLine = computed(() =>
  manualUsernames.value.some(x => x.includes(' ')),
);

const allSelectedReviewers = computed(() => {
  const nonDuo = selectedReviewers.value.filter(r => r !== 'duo-ai');

  return [...new Set([...nonDuo, ...manualUsernames.value])];
});

const reviewerOptions = computed(() => {
  const suggestedAlreadyListed = props.otherReviewers.some(
    reviewer => reviewer.toLowerCase() === props.suggestedReviewer.toLowerCase(),
  );
  const reviewers = suggestedAlreadyListed
    ? props.otherReviewers
    : [...props.otherReviewers, props.suggestedReviewer];

  return [...reviewers, 'duo-ai'];
});

const includeDuo = computed(() => selectedReviewers.value.includes('duo-ai'));
const tooManyReviewers = computed(() => allSelectedReviewers.value.length > MAX_REVIEWERS);
const isValid = computed(() =>
  (allSelectedReviewers.value.length > 0 || includeDuo.value)
  && !hasSpacesInLine.value
  && !tooManyReviewers.value,
);
const state = computed(() => (isValid.value ? null : false));

const handleError = () => {
  loading.value = false;
  emits('error');
};

const requestReview = async () => {
  if (!isValid.value || loading.value) {
    return;
  }

  loading.value = true;

  try {
    await axios.post('/api/v1/manage_merge_request/request_review', {
      project_id: props.projectId,
      merge_request_iid: props.mergeRequestIid,
      include_duo: includeDuo.value,
      reviewers: allSelectedReviewers.value,
    });
  } catch {
    handleError();

    return;
  }

  window.location.href = props.webUrl;
};

onMounted(() => {
  if (props.otherReviewers.length > 0) {
    return;
  }

  selectedReviewers.value = props.duoHasReviewed && props.suggestedReviewer
    ? [props.suggestedReviewer]
    : ['duo-ai'];
});
</script>

<template>
  <BForm
    novalidate
    @submit.prevent="requestReview"
  >
    <BFormGroup
      label="Request a review or re-review from specific users:"
      class="mb-3"
    >
      <BFormTextarea
        v-model="customReviewers"
        rows="2"
        max-rows="4"
        placeholder="Add specific reviewers (one username per line)"
        class="mb-3"
        :state="customReviewers && (hasSpacesInLine ? false : null)"
      />
      <BFormInvalidFeedback
        v-if="hasSpacesInLine"
        :state="false"
        class="mb-2"
      >
        Each line must contain a single username with no spaces.
      </BFormInvalidFeedback>

      <div class="d-flex flex-column gap-2">
        <div
          v-for="reviewer in reviewerOptions"
          :key="reviewer"
        >
          <BFormCheckbox
            v-model="selectedReviewers"
            :value="reviewer"
            :state="state"
          >
            <template v-if="reviewer === 'duo-ai'">
              Request review from GitLab Duo AI
            </template>
            <template v-else-if="props.otherReviewers.includes(reviewer)">
              Re-request review from current reviewer
              <a
                :href="`https://gitlab.com/${reviewer}`"
                target="_blank"
              >@{{ reviewer }}
              </a>
            </template>
            <template v-else>
              Request review from suggested reviewer
              <a
                :href="`https://gitlab.com/${reviewer}`"
                target="_blank"
              >@{{ reviewer }}
              </a>
            </template>
          </BFormCheckbox>
        </div>
        <BFormInvalidFeedback
          v-if="tooManyReviewers"
          :state="false"
        >
          You can request a review from at most {{ MAX_REVIEWERS }} reviewers at a time.
        </BFormInvalidFeedback>
        <BFormInvalidFeedback
          v-else-if="!isValid && !hasSpacesInLine"
          :state="state"
        >
          Select a reviewer or enter a username to request a review.
        </BFormInvalidFeedback>
      </div>
    </BFormGroup>

    <LoadingButton
      type="submit"
      variant="primary"
      :loading="loading"
      loading-text="Requesting..."
    >
      Request review
    </LoadingButton>
  </BForm>
</template>
